import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { LanguageProvider } from "@/lib/i18n";
import Landing from "@/pages/Landing";
import Guest from "@/pages/Guest";
import RequireOwner from "@/pages/RequireOwner";

// Admin routes are lazy-loaded so a guest opening /g/:token does not
// download the dashboard, link-detail editor, OCR, or feedback console
// just to see their own welcome page. RequireOwner stays eager because it
// wraps the admin routes and runs the session check that precedes them.
// Suspense fallback is null to match RequireOwner's own "checking" state,
// which already renders nothing -- avoids a flash of spinner during the
// chunk fetch.
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminHub = lazy(() => import("@/pages/AdminHub"));
const AdminPortal = lazy(() => import("@/pages/AdminPortal"));
const AdminApartment = lazy(() => import("@/pages/AdminApartment"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminLinkDetail = lazy(() => import("@/pages/AdminLinkDetail"));
const AdminFeedback = lazy(() => import("@/pages/AdminFeedback"));

function LegacyLinkRedirect() {
  const params = new URLSearchParams(window.location.search);
  const id = window.location.pathname.split("/").pop() ?? "";
  return (
    <Navigate
      to={`/admin/guests/${id}${params.toString() ? `?${params}` : ""}`}
      replace
    />
  );
}

function useDismissToastsOnClick() {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest("button, a, [role='button']")) return;
      if (target.closest("[data-sonner-toast]")) {
        toast.dismiss();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);
}

export default function App() {
  useDismissToastsOnClick();
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/g/:token" element={<Guest />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <RequireOwner>
                  <AdminHub />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/portal"
              element={
                <RequireOwner>
                  <AdminPortal />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/apartment"
              element={
                <RequireOwner>
                  <AdminApartment />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/guests"
              element={
                <RequireOwner>
                  <AdminDashboard />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/guests/:id"
              element={
                <RequireOwner>
                  <AdminLinkDetail />
                </RequireOwner>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <RequireOwner>
                  <AdminFeedback />
                </RequireOwner>
              }
            />
            <Route path="/admin/links/:id" element={<LegacyLinkRedirect />} />
            <Route
              path="/admin/links"
              element={<Navigate to="/admin/guests" replace />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster
          richColors
          position="top-center"
          toastOptions={{ style: { cursor: "pointer" } }}
        />
      </BrowserRouter>
    </LanguageProvider>
  );
}
